import { Suspense, lazy, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { HeroLanding } from '@/components/ui/hero-1';
import { Header } from '@/components/Header';
import Footer from '@/components/Footer';
import { ProjectNameDialog } from '@/components/ProjectNameDialog';
import { ProjectSelectionDialog } from '@/components/ProjectSelectionDialog';
import { ProjectList } from '@/components/ProjectList';
import { QuickStartCameras } from '@/components/QuickStartCameras';
import { SavePromptDialog, PromptType } from '@/components/SavePromptDialog';
import { SEOHead } from '@/components/SEOHead';
import { useProjects } from '@/hooks/useProjects';

import { useAuth } from '@/contexts/AuthContext';
import { usePlanLimits } from '@/hooks/usePlanLimits';
import { LogoCloud } from '@/components/ui/logo-cloud';
import { Camera } from '@/data/cameras';
import { toast } from '@/lib/toast';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

// Home page components
// Keep above-the-fold bits eagerly loaded; lazy-load the rest to improve FCP.
import { ScrollReveal } from '@/components/home/ScrollReveal';

const HowItWorks = lazy(() => import('@/components/home/HowItWorks').then((m) => ({ default: m.HowItWorks })));
const FeatureShowcase = lazy(() => import('@/components/home/FeatureShowcase').then((m) => ({ default: m.FeatureShowcase })));
const AiAssistantDemo = lazy(() => import('@/components/home/AiAssistantDemo').then((m) => ({ default: m.AiAssistantDemo })));
const SignalFlowShowcase = lazy(() => import('@/components/home/SignalFlowShowcase').then((m) => ({ default: m.SignalFlowShowcase })));
const RateFinderShowcase = lazy(() => import('@/components/home/RateFinderShowcase').then((m) => ({ default: m.RateFinderShowcase })));
const PricingTeaser = lazy(() => import('@/components/home/PricingTeaser').then((m) => ({ default: m.PricingTeaser })));
const FAQSection = lazy(() => import('@/components/home/FAQSection').then((m) => ({ default: m.FAQSection })));

// Logos – served as static assets from /public to avoid JS module overhead
const cameraLogos = [
  { src: "/logos/sony-logo.svg", alt: "Sony" },
  { src: "/logos/red-logo.svg", alt: "RED" },
  { src: "/logos/blackmagic-logo.svg", alt: "Blackmagic" },
  { src: "/logos/arri-logo.svg", alt: "ARRI" },
  { src: "/logos/canon-logo.svg", alt: "Canon" },
  { src: "/logos/tilta-logo.svg", alt: "Tilta" },
  { src: "/logos/smallrig-logo.svg", alt: "SmallRig" },
  { src: "/logos/teradek-logo.svg", alt: "Teradek" },
  { src: "/logos/rode-logo.svg", alt: "Rode" },
  { src: "/logos/hollyland-logo.svg", alt: "Hollyland" },
];

const Index = () => {
  const navigate = useNavigate();
  const [showProjectDialog, setShowProjectDialog] = useState(false);
  const [showProjectSelectionDialog, setShowProjectSelectionDialog] = useState(false);
  const [pendingGuidedStart, setPendingGuidedStart] = useState(false);
  const [pendingCamera, setPendingCamera] = useState<Camera | null>(null);
  const [renameProjectDialog, setRenameProjectDialog] = useState<{ open: boolean; projectId: string | null }>({
    open: false,
    projectId: null,
  });
  const [deleteProjectDialog, setDeleteProjectDialog] = useState<{ open: boolean; projectId: string | null }>({
    open: false,
    projectId: null,
  });
  const [savePromptOpen, setSavePromptOpen] = useState(false);
  const [savePromptType, setSavePromptType] = useState<PromptType>('auth');
  
  const { canCreateProject, canCreateRig, maxRigsPerProject } = usePlanLimits();
  const { isAdmin } = useAuth();
  
  // Guided setup is only accessible to admins (coming soon for regular users)
  const isGuidedAccessible = isAdmin;

  const {
    projects,
    isLoading: projectsLoading,
    createProject,
    selectProject,
    deleteProject,
    updateProjectName,
    maxProjects,
  } = useProjects();

  const handleGetStarted = () => {
    setPendingGuidedStart(false);
    setPendingCamera(null);
    if (projects.length > 0) {
      setShowProjectSelectionDialog(true);
    } else {
      setShowProjectDialog(true);
    }
  };

  const handleStartGuided = () => {
    setPendingGuidedStart(true);
    setPendingCamera(null);
    if (projects.length > 0) {
      setShowProjectSelectionDialog(true);
    } else {
      setShowProjectDialog(true);
    }
  };

  const handleQuickStart = async (camera: Camera) => {
    if (projects.length > 0) {
      setPendingCamera(camera);
      setShowProjectSelectionDialog(true);
    } else {
      const projectName = `${camera.brand} ${camera.name} Build`;
      const result = await createProject(projectName);
      if (result.success) {
        navigate(`/project/${result.project.id}/build`, { state: { camera } });
      } else {
        setSavePromptType('project-limit');
        setSavePromptOpen(true);
      }
    }
  };

  const handleCreateProject = async (name: string) => {
    const result = await createProject(name);
    setShowProjectDialog(false);
    
    if (result.success) {
      if (pendingGuidedStart) {
        setPendingGuidedStart(false);
        navigate(`/guided/${result.project.id}`);
      } else if (pendingCamera) {
        navigate(`/project/${result.project.id}/build`, { state: { camera: pendingCamera } });
        setPendingCamera(null);
      } else {
        navigate(`/project/${result.project.id}/new`);
      }
    } else {
      setSavePromptType('project-limit');
      setSavePromptOpen(true);
    }
  };

  const handleSelectExistingProject = async (project: typeof projects[0]) => {
    if (!canCreateRig(project.id)) {
      setShowProjectSelectionDialog(false);
      setSavePromptType('rig-limit');
      setSavePromptOpen(true);
      return;
    }
    
    await selectProject(project.id);
    setShowProjectSelectionDialog(false);
    
    if (pendingGuidedStart) {
      setPendingGuidedStart(false);
      navigate(`/guided/${project.id}`);
    } else if (pendingCamera) {
      navigate(`/project/${project.id}/build`, { state: { camera: pendingCamera } });
      setPendingCamera(null);
    } else {
      navigate(`/project/${project.id}/new`);
    }
  };

  const handleCreateNewFromSelection = () => {
    setShowProjectSelectionDialog(false);
    setShowProjectDialog(true);
  };

  const handleProjectLimitReached = () => {
    setShowProjectSelectionDialog(false);
    setSavePromptType('project-limit');
    setSavePromptOpen(true);
  };

  const handleSelectProject = async (project: typeof projects[0]) => {
    await selectProject(project.id);
    navigate(`/project/${project.id}`);
  };

  const handleRenameProject = async (name: string) => {
    if (renameProjectDialog.projectId) {
      await updateProjectName(renameProjectDialog.projectId, name);
      setRenameProjectDialog({ open: false, projectId: null });
      toast.success('Project renamed');
    }
  };

  const handleDeleteProject = async () => {
    if (deleteProjectDialog.projectId) {
      await deleteProject(deleteProjectDialog.projectId);
      setDeleteProjectDialog({ open: false, projectId: null });
      toast.success('Project deleted');
    }
  };

  const currentProjectForRename = projects.find(p => p.id === renameProjectDialog.projectId);

  return (
    <div className="min-h-screen bg-background">
      <SEOHead path="/" />
      <Header onLogoClick={() => navigate('/')} />
      
      {/* Hero Section */}
      <div className="relative">
        <HeroLanding
          title="Build Your Perfect Camera Rig"
          titleHighlight="Perfect"
          description="Stop guessing your rig. Build cinema setups that are compatible, powered, and ready to roll."
          announcementBanner={{
            text: "AI-Powered Rig Assistant",
            linkText: "Try It",
            onClick: () => window.dispatchEvent(new CustomEvent('open-rig-assistant'))
          }}
          callToActions={[
            {
              text: "Guided Setup",
              onClick: isAdmin ? () => navigate('/guided') : () => {},
              variant: 'secondary' as const,
              disabled: !isAdmin,
              badge: isAdmin ? undefined : 'Coming Soon'
            },
            {
              text: 'Start Building',
              onClick: handleGetStarted,
               variant: 'primary' as const,
            }
          ]}
          titleSize="large"
          intensity="medium"
          className="min-h-screen"
          showScrollCta
        />
      </div>

      {/* Logo Cloud */}
      <section className="py-12 sm:py-16 border-b border-border/30">
        <ScrollReveal>
          <div className="container mx-auto max-w-4xl px-4 sm:px-6 text-center mb-8">
            <h2 className="text-lg sm:text-xl font-display font-semibold text-muted-foreground mb-2">
              Supports Equipment From Leading Brands
            </h2>
          </div>
        </ScrollReveal>
        <ScrollReveal delay={0.1}>
          <LogoCloud logos={cameraLogos} className="opacity-80" />
        </ScrollReveal>
      </section>

      {/* How It Works (The Plan) */}
      <Suspense fallback={null}>
        <HowItWorks />
      </Suspense>

      {/* Why Professionals Choose CineRig */}
      <Suspense fallback={null}>
        <FeatureShowcase />
      </Suspense>

      {/* AI Assistant Demo */}
      <Suspense fallback={null}>
        <AiAssistantDemo />
      </Suspense>

      {/* Signal Flow Showcase */}
      <Suspense fallback={null}>
        <SignalFlowShowcase />
      </Suspense>

      {/* Quick Start Cameras (Early Action) */}
      <section className="py-16 px-6 border-t border-border/30">
        <div className="container mx-auto max-w-3xl">
          <ScrollReveal>
            <div className="text-center mb-8">
              <h2 className="text-2xl md:text-3xl font-display mb-2">
                Popular Cameras to <span className="text-primary">Start With</span>
              </h2>
              <p className="text-muted-foreground">
                Quick start with your favorite camera
              </p>
            </div>
          </ScrollReveal>
          <ScrollReveal delay={0.15}>
            <QuickStartCameras onQuickStart={handleQuickStart} />
          </ScrollReveal>
        </div>
      </section>

      {/* Rate Finder Showcase */}
      <Suspense fallback={null}>
        <RateFinderShowcase />
      </Suspense>

      {/* Pricing Teaser */}
      <Suspense fallback={null}>
        <PricingTeaser />
      </Suspense>

      {/* Recent Projects */}
      {projects.length > 0 && (
        <section className="py-12 px-6 bg-card/20 border-t border-border/30">
          <div className="container mx-auto max-w-2xl">
            <ScrollReveal>
              <div className="text-center mb-6">
                <h2 className="text-xl font-display font-semibold">Recent Projects</h2>
                <p className="text-sm text-muted-foreground">Continue where you left off</p>
              </div>
            </ScrollReveal>
            <ScrollReveal delay={0.1}>
              <ProjectList
                projects={projects}
                onSelectProject={handleSelectProject}
                onRenameProject={(id) => setRenameProjectDialog({ open: true, projectId: id })}
                onDeleteProject={(id) => setDeleteProjectDialog({ open: true, projectId: id })}
              />
            </ScrollReveal>
          </div>
        </section>
      )}

      {/* FAQ Section */}
      <Suspense fallback={null}>
        <FAQSection />
      </Suspense>

      {/* Footer */}
      <Footer />

      {/* Dialogs */}
      <ProjectSelectionDialog
        open={showProjectSelectionDialog}
        onOpenChange={setShowProjectSelectionDialog}
        projects={projects}
        onSelectProject={handleSelectExistingProject}
        onCreateNew={handleCreateNewFromSelection}
        canCreateProject={canCreateProject}
        onLimitReached={handleProjectLimitReached}
      />

      <ProjectNameDialog
        open={showProjectDialog}
        onOpenChange={setShowProjectDialog}
        onSubmit={handleCreateProject}
      />

      <ProjectNameDialog
        open={renameProjectDialog.open}
        onOpenChange={(open) => setRenameProjectDialog({ open, projectId: open ? renameProjectDialog.projectId : null })}
        onSubmit={handleRenameProject}
        title="Rename Project"
        defaultValue={currentProjectForRename?.name || ''}
        submitLabel="Save"
      />

      <AlertDialog
        open={deleteProjectDialog.open}
        onOpenChange={(open) => setDeleteProjectDialog({ open, projectId: open ? deleteProjectDialog.projectId : null })}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Project?</AlertDialogTitle>
            <AlertDialogDescription>
              This will delete the project and all its rigs. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteProject} className="bg-destructive hover:bg-destructive/90">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <SavePromptDialog
        open={savePromptOpen}
        onOpenChange={setSavePromptOpen}
        promptType={savePromptType}
        maxProjects={maxProjects}
        maxRigsPerProject={maxRigsPerProject}
      />
    </div>
  );
};

export default Index;
